import { z } from 'zod/v4';

import { getNormalizedFileName } from '../export/exportFileName';
import { assertSuccessfulResponseGenericErrorCode } from '../http/assertSuccessfulResponseGenericErrorCode';
import { endpoint } from '../http/endpoint';
import { AssemblyWithShareRuntype, OutlineRuntype, PCBV2Runtype, PCBV2SpecificationRuntype } from './pcbBackendTypes';
import { PCBV2SpecificationCapabilitiesRuntype } from './pcbCapabilityBackendTypes';

/**
 * Provider-side reads of a shared board. In the two-tenant pricing flow the price provider holds only a
 * SharedAssembly row, so every normal PCB read 404s for it ("Access to this Entity is denied") and these
 * share-scoped variants are the only way for it to see the board it was asked to quote. All of them are
 * team-checked server-side on top of the action permission, so a guessed share UUID returns 404.
 */
export const pcbShareEndpoints = {
    'GET /assembly/shares/:share': endpoint({
        description: 'The shared assembly and the share itself, by share id.',
        pathParams: z.object({ share: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: AssemblyWithShareRuntype,
        actionPermission: () => 'CanViewPcb',
    }),

    'GET /ems/pcb/v2/shares/:share': endpoint({
        description: 'The shared PCB, by share id.',
        pathParams: z.object({ share: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: PCBV2Runtype,
        actionPermission: () => 'CanViewPcb',
    }),

    'GET /ems/pcb/v2/shares/:share/specifications': endpoint({
        description: 'Lists the specifications of the shared PCB.',
        pathParams: z.object({ share: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(PCBV2SpecificationRuntype),
        actionPermission: () => 'CanViewPcb',
    }),

    'GET /ems/pcb/v2/shares/:share/specifications/:specification': endpoint({
        description: 'A single specification of the shared PCB, by name or id.',
        pathParams: z.object({ share: z.string(), specification: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: PCBV2SpecificationRuntype,
        actionPermission: () => 'CanViewPcb',
    }),

    'GET /ems/pcb/v2/shares/:share/capabilities': endpoint({
        description:
            'The allowed values and settings for every field of the shared PCB. The four overrides behave as on the pcbs/:pcbId variant: they recompute the material-number options and the implied fields for an in-progress vendor or material without saving the spec first.',
        pathParams: z.object({ share: z.string() }),
        queryParams: z.object({
            materialVendor: z.string().optional(),
            materialNumber: z.string().optional(),
            soldermaskMaterialVendor: z.string().optional(),
            soldermaskMaterialNumber: z.string().optional(),
        }),
        requestBody: z.undefined(),
        responseBody: PCBV2SpecificationCapabilitiesRuntype,
        actionPermission: () => 'CanViewPcbCapabilities',
    }),

    'GET /ems/pcb/v2/shares/:share/outline': endpoint({
        description: 'The chosen outline of the shared PCB.',
        pathParams: z.object({ share: z.string() }),
        queryParams: z.object({ withGraphic: z.boolean().optional() }),
        requestBody: z.undefined(),
        responseBody: OutlineRuntype,
        actionPermission: () => 'CanViewPcb',
    }),

    'GET /ems/pcb/v2/shares/:share/outlines': endpoint({
        description: 'The outline candidates of the shared PCB.',
        pathParams: z.object({ share: z.string() }),
        queryParams: z.object({ withGraphic: z.boolean().optional() }),
        requestBody: z.undefined(),
        responseBody: z.array(OutlineRuntype),
        actionPermission: () => 'CanViewPcb',
    }),

    'GET /ems/pcb/v2/shares/:share/files/all': endpoint({
        description:
            'Downloads the files of the shared PCB as a zip. Serves the uploaded archive directly when it is a single zip, otherwise packs the original files (or, for older boards without originals, the processed ones).',
        pathParams: z.object({ share: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
        actionPermission: () => 'CanViewPcb',
    }),
};
